import React, { useState, useRef } from 'react';
import {
  VStack, FormControl, FormLabel, Input, Textarea, Button,
  SimpleGrid, useToast, HStack, Text, Box, Icon, Badge,
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiImage, FiTrash2 } from 'react-icons/fi';
import axiosInstance from '../../../api/AxiosInstance';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

interface Props {
  promotor?: any;
  onGuardado: () => void;
}

const FormularioPromotor: React.FC<Props> = ({ promotor, onGuardado }) => {
  const toast = useToast();
  const cvRef = useRef<HTMLInputElement>(null);
  const fotoRef = useRef<HTMLInputElement>(null);
  const [guardando, setGuardando] = useState(false);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [fotoFile, setFotoFile] = useState<File | null>(null);

  const cvExistente = (promotor?.archivos || []).find((a: any) => a.tipoArchivo === 'cv');
  const fotoExistente = (promotor?.archivos || []).find((a: any) => a.tipoArchivo === 'foto');

  const [form, setForm] = useState({
    nombre: promotor?.nombre || '',
    curp: promotor?.curp || '',
    rfc: promotor?.rfc || '',
    direccion: promotor?.direccion || '',
    telefono: promotor?.telefono || '',
    correo: promotor?.correo || '',
    giro: promotor?.giro || '',
    temas: promotor?.temas || '',
    estudios: promotor?.estudios || '',
    especialidad: promotor?.especialidad || '',
    trayectoria: promotor?.trayectoria || '',
  });

  const set = (f: string, v: string) => setForm((p) => ({ ...p, [f]: v }));

  const handleGuardar = async () => {
    if (!form.nombre) {
      toast({ title: 'El nombre es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, String(v)));
      if (cvFile) formData.append('cv', cvFile);
      if (fotoFile) formData.append('foto', fotoFile);

      if (promotor?.id) {
        await axiosInstance.put(`/cultura/promotores/${promotor.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast({ title: 'Promotor actualizado', status: 'success', duration: 3000 });
      } else {
        await axiosInstance.post('/cultura/promotores', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast({ title: 'Promotor registrado', status: 'success', duration: 3000 });
      }
      onGuardado();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'Error al guardar', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Nombre completo</FormLabel>
          <Input value={form.nombre} onChange={(e) => set('nombre', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">CURP</FormLabel>
          <Input value={form.curp} onChange={(e) => set('curp', e.target.value.toUpperCase())} maxLength={18} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">RFC</FormLabel>
          <Input value={form.rfc} onChange={(e) => set('rfc', e.target.value.toUpperCase())} maxLength={13} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Teléfono</FormLabel>
          <Input value={form.telefono} onChange={(e) => set('telefono', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Correo electrónico</FormLabel>
          <Input type="email" value={form.correo} onChange={(e) => set('correo', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Giro cultural</FormLabel>
          <Input value={form.giro} onChange={(e) => set('giro', e.target.value)} placeholder="Ej. Danza, Música, Artes plásticas" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Especialidad</FormLabel>
          <Input value={form.especialidad} onChange={(e) => set('especialidad', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estudios</FormLabel>
          <Input value={form.estudios} onChange={(e) => set('estudios', e.target.value)} placeholder="Nivel de estudios" />
        </FormControl>
      </SimpleGrid>

      <FormControl>
        <FormLabel fontSize="sm">Dirección</FormLabel>
        <Input value={form.direccion} onChange={(e) => set('direccion', e.target.value)} />
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm">Temas que imparte</FormLabel>
        <Textarea rows={2} value={form.temas} onChange={(e) => set('temas', e.target.value)}
          placeholder="Lista de temas culturales que maneja..." />
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm">Trayectoria</FormLabel>
        <Textarea rows={4} value={form.trayectoria} onChange={(e) => set('trayectoria', e.target.value)}
          placeholder="Descripción de la trayectoria artística y cultural..." />
      </FormControl>

      {/* Archivos */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
          <Text fontSize="sm" fontWeight="bold" mb={2} color="#5b182b">CV (PDF)</Text>
          {cvExistente && !cvFile && (
            <HStack mb={2}>
              <Icon as={FiFile} color="red.500" />
              <Text fontSize="xs" color="gray.600">CV existente</Text>
            </HStack>
          )}
          {cvFile && (
            <HStack mb={2}>
              <Icon as={FiFile} color="green.500" />
              <Text fontSize="xs">{cvFile.name}</Text>
              <Badge colorScheme="green">Nuevo</Badge>
              <Button size="xs" variant="ghost" colorScheme="red" onClick={() => setCvFile(null)}>
                <Icon as={FiTrash2} />
              </Button>
            </HStack>
          )}
          <input ref={cvRef} type="file" accept="application/pdf" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) setCvFile(f); e.target.value = ''; }} />
          <Button size="xs" leftIcon={<Icon as={FiUpload} />} variant="outline" colorScheme="red"
            onClick={() => cvRef.current?.click()}>
            {cvExistente ? 'Reemplazar CV' : 'Subir CV'}
          </Button>
        </Box>

        <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
          <Text fontSize="sm" fontWeight="bold" mb={2} color="#5b182b">Fotografía</Text>
          {fotoExistente && !fotoFile && (
            <Box mb={2} w="60px" h="60px" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200">
              <img src={`${API}${fotoExistente.rutaArchivo}`} alt="foto"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </Box>
          )}
          {fotoFile && (
            <HStack mb={2}>
              <Icon as={FiImage} color="green.500" />
              <Text fontSize="xs">{fotoFile.name}</Text>
              <Badge colorScheme="green">Nueva</Badge>
              <Button size="xs" variant="ghost" colorScheme="red" onClick={() => setFotoFile(null)}>
                <Icon as={FiTrash2} />
              </Button>
            </HStack>
          )}
          <input ref={fotoRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) setFotoFile(f); e.target.value = ''; }} />
          <Button size="xs" leftIcon={<Icon as={FiUpload} />} variant="outline" colorScheme="teal"
            onClick={() => fotoRef.current?.click()}>
            {fotoExistente ? 'Reemplazar foto' : 'Subir foto'}
          </Button>
        </Box>
      </SimpleGrid>

      <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar}
        isLoading={guardando} loadingText="Guardando...">
        {promotor?.id ? 'Actualizar promotor' : 'Registrar promotor'}
      </Button>
    </VStack>
  );
};

export default FormularioPromotor;
